<template>
  <div class="apple-style">
    <!-- 背景动画元素 -->
    <div class="animated-background">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>
    
    <el-container style="position: relative; z-index: 1;">
      <!-- 简洁的header -->
      <el-header class="apple-header">
        <h1>{{ this.prodHeader }}</h1>
      </el-header>

      <el-main>
        <!-- 加载状态 -->
        <template v-if="!products.length">
          <div class="loading-state">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">Loading products...</p>
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </template>

        <!-- 产品展示区域 -->
        <template v-else>
          <div v-for="(product, index) in products" :key="index" class="product-section">
            <!-- 产品标题 -->
            <section class="hero-section" :style="getHeroStyle(product)">
              <!-- 如果有 prodSku，显示原有样式 -->
              <template v-if="product.prodSku">
                <h1 class="hero-title">{{ product.prodTitle }}</h1>
                <div class="price-container">
                  <div class="price-tag" v-if="product.prodPrice">
                    <span class="currency">$</span>
                    <span class="amount">{{ product.prodPrice }}</span>
                  </div>
                  <div class="currency" v-if="product.prodSku">
                    <span class="amount">SKU：{{ product.prodSku }}</span>
                    <br>
                    <span class="amount">UPC：{{ product.prodUpc }}</span>
                  </div>
                </div>
                <div class="cta-buttons">
                  <el-button class="cart-btn" @click="addToCart(product, $event)">
                    <i class="el-icon-shopping-cart-2"></i>
                    Add to Cart
                  </el-button>
                </div>
              </template>
              <!-- 如果没有 prodSku，显示新样式 -->
              <template v-else>
                <h1 class="hero-title">{{ product.prodTitle }}</h1>
                <div class="price-container">
                  <div class="price-tag" v-if="product.prodPrice">
                    <span class="currency">$</span>
                    <span class="amount">{{ product.prodPrice }}</span>
                  </div>
                  <div class="price-details" v-if="product.prodOriginalPrice">
                    <span class="original-price">List Price: ${{ product.prodOriginalPrice }}</span>
                    <span class="discount-tag" v-if="product.prodDiscount">{{ product.prodDiscount }}% OFF</span>
                  </div>
                </div>
                <div class="cta-buttons">
                  <el-button class="learn-more-btn" @click="learnMore(product)">Learn more</el-button>
                </div>
              </template>
            </section>

            <!-- 视频展示区 -->
            <section class="video-section" v-if="product.prodVideourl">
              <div class="video-wrapper">
                <iframe
                  :src="product.prodVideourl"
                  frameborder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  loading="lazy"
                  scrolling="no"
                ></iframe>
              </div>
            </section>

            <!-- 大图产品展示 -->
            <section class="product-showcase">
              <div class="image-grid">
                <div v-for="(item, imgIndex) in product.prodImgurl"
                     :key="imgIndex"
                     class="image-container"
                     @click="handleImageClick(index, imgIndex)"
                >
                  <img :src="item.src" :alt="item.alt" class="product-image" />
                </div>
                <!-- 添加末尾占位元素 -->
                <div class="image-container-spacer"></div>
              </div>
              <div class="gallery-controls">
                <el-button circle class="gallery-nav-btn" @click="scrollGallery('left', index)">
                  <i class="el-icon-arrow-left"></i>
                </el-button>
                <el-button circle class="gallery-nav-btn" @click="scrollGallery('right', index)">
                  <i class="el-icon-arrow-right"></i>
                </el-button>
              </div>
            </section>

            <!-- 产品详情 -->
            <section class="product-details">
              <div class="content-container">
                <h2>About this item</h2>
                <div class="content-wrapper" v-html="formatContent(product.prodContent)"></div>
              </div>
            </section>

            <!-- 产品分隔线 -->
            <div class="product-divider" v-if="index < products.length - 1">
              <div class="divider-line"></div>
              <div class="divider-text">Next Product</div>
              <div class="divider-line"></div>
            </div>
          </div>
        </template>

        <!-- 联系我们 -->
        <section class="contact-section">
          <h2>Contact Us</h2>
          <p>Thank you for your support and we sincerely invite you to be our product experience officer. Product Experience Officers can obtain our products for free. After using them, please give us feedback on your actual usage experience.</p>
          <el-button class="contact-btn" @click="sendEmail">{{ prodEmail }}</el-button>
        </section>

        <!-- 添加图片预览对话框 -->
        <el-dialog
          :visible.sync="previewVisible"
          :append-to-body="true"
          custom-class="preview-dialog"
          :show-close="false"
          @click.native="closePreview"
        >
          <img :src="currentPreviewImage" class="preview-image" />
          <div class="preview-close" @click="closePreview">
            <i class="el-icon-close"></i>
          </div>
          <div class="preview-controls">
            <el-button circle class="preview-nav-btn" @click.stop="prevImage" v-show="previewIndex > 0">
              <i class="el-icon-arrow-left"></i>
            </el-button>
            <el-button circle class="preview-nav-btn" @click.stop="nextImage" v-show="previewIndex < currentProductImages.length - 1">
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </el-dialog>
      </el-main>
    </el-container>

    <!-- 添加购物车动画组件 -->
    <div class="cart-animation" :class="{ 'animate': showCartAnimation }" :style="cartStyle">
      <i class="el-icon-shopping-cart-2"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: "CompanyHome",
  data() {
    return {
      prodHeader: '',
      prodEmail: '',
      products: [],
      previewVisible: false,
      previewIndex: 0,
      currentProductIndex: 0,
      isAddingToCart: false,
      showCartAnimation: false,
      cartStyle: {
        left: '0px',
        top: '0px'
      },
    };
  },
  computed: {
    currentProductImages() {
      return this.products[this.currentProductIndex]?.prodImgurl || [];
    },
    currentPreviewImage() {
      return this.currentProductImages[this.previewIndex]?.src || '';
    },
    currentProduct() {
      return this.products[this.currentProductIndex] || {};
    }
  },
  mounted() {
    this.init();
    this.select();
    this.initScrollObserver();
    this.initParallaxEffect();

    // 为段落添加延迟动画
    document.querySelectorAll('.content-paragraph').forEach((p, index) => {
      p.style.setProperty('--paragraph-index', index);
    });
  },
  methods: {
    init() {
      const params = {
        visitDomain: window.location.hostname,
      }
      this.$axios.post('/visit/insert', params).then(response => {
        if (response.data.code === 0) {
          console.log();
        }
      }).catch(error => {
        console.error(error);
      });
    },
    select() {
      const params = {
        prodName: window.location.hostname.split('.')[1],
      }
      this.$axios.post('/prod/list', params).then(response => {
        if (response.data.code === 0) {
          const data = response.data.data;
          this.prodHeader = data[0].prodHeader;
          this.prodEmail = data[0].prodEmail;

          // 处理产品数据
          this.products = data.map(item => {
            const imgurl = item.prodImgurl.split(",");
            const prodImgurlmap = imgurl.map((src, index) => ({
              src: src,
              alt: '描述' + (index + 1)
            }));

            return {
              ...item,
              prodImgurl: prodImgurlmap
            };
          });

          // 设置第一个产品的第一张图片为hero背景
          if (this.products.length > 0 && this.products[0].prodImgurl.length > 0) {
            this.$nextTick(() => {
              const heroSection = document.querySelector('.hero-section');
              if (heroSection) {
                heroSection.style.setProperty('--hero-bg-image', `url(${this.products[0].prodImgurl[0].src})`);
              }
            });
          }

          document.title = data[0].prodName;
        }
      }).catch(error => {
        console.error(error);
      });
    },
    sendEmail() {
      const subject = encodeURIComponent("邮件主题"); // 邮件主题
      const body = encodeURIComponent("这是邮件正文。"); // 邮件正文
      const recipient = this.prodEmail; // 收件人邮箱

      // 创建mailto链接
      const mailtoLink = `mailto:${recipient}?subject=${subject}&body=${body}`;

      // 打开邮件客户端
      window.location.href = mailtoLink;
    },
    addToCart(product, event) {
      if (this.isAddingToCart) return;
      this.isAddingToCart = true;

      // 获取按钮位置
      const button = event.currentTarget;
      const buttonRect = button.getBoundingClientRect();

      // 设置动画起始位置
      this.cartStyle = {
        left: `${buttonRect.left + buttonRect.width / 2}px`,
        top: `${buttonRect.top + buttonRect.height / 2}px`
      };

      // 显示动画
      this.showCartAnimation = true;

      // 动画结束后重置状态
      setTimeout(() => {
        this.showCartAnimation = false;
        this.isAddingToCart = false;
        console.log('Added to cart:', product.prodTitle);
      }, 1500);
    },
    learnMore(product) {
      if (product.prodAmazonurl) {
        window.open(product.prodAmazonurl, "_blank"); // 在新窗口中打开
      }
    },
    initScrollObserver() {
      const options = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      }, options);

      // 观察所有需要动画的元素
      document.querySelectorAll('.scroll-trigger, .video-section, .product-showcase').forEach(el => {
        observer.observe(el);
      });
    },
    initParallaxEffect() {
      const hero = document.querySelector('.hero-section');
      if (!hero) return;

      window.addEventListener('mousemove', (e) => {
        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;

        const moveX = (clientX - innerWidth / 2) * 0.01;
        const moveY = (clientY - innerHeight / 2) * 0.01;

        hero.style.transform = `translate3d(${moveX}px, ${moveY}px, 0)`;
      });
    },
    formatContent(content) {
      // 将内容按段落分割
      const paragraphs = content.split('\n');
      return paragraphs
        .map(p => p.trim())
        .filter(p => p.length > 0)
        .map(p => `<p class="content-paragraph">${p}</p>`)
        .join('');
    },
    handleImageClick(productIndex, imageIndex) {
      this.currentProductIndex = productIndex;
      this.previewIndex = imageIndex;
      this.previewVisible = true;
    },
    closePreview() {
      this.previewVisible = false;
    },
    prevImage() {
      if (this.previewIndex > 0) {
        this.previewIndex--;
      }
    },
    nextImage() {
      if (this.previewIndex < this.currentProductImages.length - 1) {
        this.previewIndex++;
      }
    },
    scrollGallery(direction, productIndex) {
      const galleries = document.querySelectorAll('.image-grid');
      const gallery = galleries[productIndex];
      if (!gallery) return;

      const imageContainer = gallery.querySelector('.image-container');
      const scrollAmount = imageContainer ? imageContainer.offsetWidth + 40 : 800;

      const scrollPosition = direction === 'left'
        ? gallery.scrollLeft - scrollAmount
        : gallery.scrollLeft + scrollAmount;

      gallery.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    getHeroStyle(product) {
      if (product.prodImgurl && product.prodImgurl.length > 0) {
        return {
          '--hero-bg-image': `url(${product.prodImgurl[0].src})`
        };
      }
      return {};
    }
  }
};
</script>

<style scoped>
.apple-style {
  background-color: #fff;
  color: #1d1d1f;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif;
  animation: pageLoad 1s ease-out;
  overflow-x: hidden; /* 防止水平滚动 */
  position: relative;
}

@keyframes pageLoad {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 背景动画效果 */
.animated-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  top: -250px;
  left: -250px;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  top: 50%;
  right: -200px;
  animation-delay: 5s;
}

.orb-3 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  bottom: -300px;
  left: 30%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(100px, -100px) scale(1.1);
  }
  66% {
    transform: translate(-50px, 50px) scale(0.9);
  }
}

.apple-header {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  z-index: 100;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  animation: headerSlideDown 0.6s ease-out;
  overflow: hidden;
}

.apple-header::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  animation: headerShine 3s ease-in-out infinite;
}

@keyframes headerShine {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

@keyframes headerSlideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.apple-header:hover {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.apple-header h1 {
  font-size: 21px;
  font-weight: 400;
  margin: 0;
  color: #1d1d1f;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  background: linear-gradient(90deg, #1d1d1f, #0071e3, #1d1d1f);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  animation: headerTextShimmer 3s linear infinite;
}

@keyframes headerTextShimmer {
  to {
    background-position: 200% center;
  }
}

.apple-header h1:hover {
  transform: scale(1.05) translateY(-2px);
  -webkit-text-fill-color: transparent;
  letter-spacing: 1px;
}

.hero-section {
  height: 93vh;
  margin-bottom: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, #f5f5f7 0%, #fff 100%);
  position: relative;
  overflow: hidden;
  --hero-bg-image: none;
  transition: transform 0.5s ease-out;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle at center,
    rgba(0, 113, 227, 0.03) 0%,
    transparent 70%
  );
  animation: heroGlow 8s ease-in-out infinite;
  pointer-events: none;
}

@keyframes heroGlow {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(10%, 10%) scale(1.1);
    opacity: 0.8;
  }
}

.hero-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: var(--hero-bg-image);
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.8;
  z-index: 0;
  transition: all 0.5s ease;
  animation: heroImageFade 2s ease-out forwards, heroImageFloat 6s ease-in-out infinite 2s;
}

@keyframes heroImageFade {
  0% {
    opacity: 0;
    transform: scale(1.1) rotate(1deg);
  }
  100% {
    opacity: 0.8;
    transform: scale(1) rotate(0deg);
  }
}

@keyframes heroImageFloat {
  0%, 100% {
    transform: scale(1) translateY(0);
  }
  50% {
    transform: scale(1.02) translateY(-10px);
  }
}

/* 确保内容在最上层 */
.hero-title, .price-container, .cta-buttons {
  position: relative;
  z-index: 3;
  text-shadow: none;
}

.hero-title {
  font-size: 64px;
  font-weight: 700;
  margin-bottom: 50px;
  color: #1d1d1f;
  animation: titleFadeIn 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
  transform: translateZ(50px);
  transition: all 0.3s ease-out;
  position: relative;
}

.hero-title::after {
  content: attr(data-text);
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: linear-gradient(90deg, #0071e3, #00d4ff, #0071e3);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.hero-title:hover {
  transform: scale(1.02) translateY(-5px);
  text-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.hero-title:hover::after {
  opacity: 0.3;
  animation: titleGradient 2s linear infinite;
}

@keyframes titleGradient {
  to {
    background-position: 200% center;
  }
}

@keyframes titleFadeIn {
  0% {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
    filter: blur(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

/* 按钮容器样式 */
.cta-buttons {
  display: flex;
  gap: 20px;
  opacity: 0;  /* 初始透明 */
  animation: titleFadeIn 1.2s ease-out forwards;  /* 使用相同的动画 */
  animation-delay: 0.6s;  /* 在价格之后显示 */
}

/* 按钮样式 */
.cart-btn {
  font-size: 17px;
  padding: 12px 28px;
  border-radius: 980px;
  background: #ff3b30 !important;
  border: none !important;
  color: #fff !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transform-origin: center;
  box-shadow: 0 4px 12px rgba(255, 59, 48, 0.3);
  animation: buttonBounce 0.6s ease-out 0.8s backwards;
}

@keyframes buttonBounce {
  0% {
    opacity: 0;
    transform: scale(0.3) translateY(50px);
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.cart-btn:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 8px 20px rgba(255, 59, 48, 0.5);
  background: #ff1a1a !important;
}

.cart-btn:active {
  transform: scale(0.95);
  box-shadow: 0 2px 6px rgba(255, 59, 48, 0.2);
}

.cart-btn i {
  font-size: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: #fff;
}

.cart-btn:hover i {
  transform: scale(1.2) rotate(15deg);
  animation: wiggle 0.5s ease-in-out;
}

@keyframes wiggle {
  0%, 100% { transform: scale(1.2) rotate(0deg); }
  25% { transform: scale(1.2) rotate(15deg); }
  75% { transform: scale(1.2) rotate(-15deg); }
}

/* 添加按钮点击波纹效果 */
.cart-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: rgba(255, 255, 255, 0.8);
  opacity: 0;
  border-radius: 100%;
  transform: scale(1, 1) translate(-50%, -50%);
  transform-origin: 50% 50%;
}

.cart-btn:active::after {
  animation: ripple 0.8s ease-out;
}

@keyframes ripple {
  0% {
    transform: scale(0, 0);
    opacity: 0.8;
  }
  100% {
    transform: scale(30, 30);
    opacity: 0;
  }
}

/* 添加按钮点击时的闪光效果 */
.cart-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.cart-btn:active::before {
  transform: translateX(100%);
}

/* 添加成功提示动画 */
@keyframes successPulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.cart-btn.success {
  animation: successPulse 0.5s ease;
  background: #34c759 !important;
}

.cart-animation {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  color: #fff;
  font-size: 32px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2997ff;
  box-shadow: 0 0 20px rgba(41, 151, 255, 0.6);
  filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.8));
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
  transition: none;
}

.cart-animation.animate {
  animation: cartFly 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes cartFly {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -300%) scale(0.8);
    opacity: 0;
  }
}

/* 确保按钮在禁用状态下保持样式 */
.cart-btn:disabled {
  background: #2997ff !important;
  opacity: 0.7;
  cursor: not-allowed;
}

/* 覆盖 Element UI 的默认样式 */
:deep(.el-button) {
  background: #2997ff !important;
  border: none !important;
  color: #fff !important;
}

:deep(.el-button:hover) {
  background: #0077ed !important;
  color: #fff !important;
}

:deep(.el-button:active) {
  background: #0077ed !important;
  color: #fff !important;
}

.buy-btn {
  font-size: 17px;
  padding: 8px 16px;
  border-radius: 980px;
  background: none;
  border: none;
  color: #2997ff;
}

/* 按钮样式 */
.learn-more-btn {
  font-size: 17px;
  padding: 12px 28px;
  border-radius: 980px;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.2);
  color: #1d1d1f;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: pointer;
  display: inline-block;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: buttonBounce 0.6s ease-out 0.8s backwards;
}

.learn-more-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 0.1),
    transparent
  );
  transition: 0.5s;
}

.learn-more-btn:hover::after {
  left: 100%;
}

.learn-more-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.3);
  transform: translateY(-4px) scale(1.05);
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.learn-more-btn:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.product-showcase {
  margin: 0;
  padding: 40px 0;
  position: relative;
  background: #fff;
  width: 100%;
  min-height: 100%;
  overflow: hidden; /* 防止溢出 */
}

.product-showcase::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 113, 227, 0.03) 1px, transparent 1px),
    linear-gradient(rgba(0, 113, 227, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
  pointer-events: none;
  opacity: 0.5;
}

@keyframes gridMove {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(50px, 50px);
  }
}

.image-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 40px;
  padding: 0 40px;
  width: 100%;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scroll-behavior: smooth;
}

/* 隐藏 WebKit 滚动条 */
.image-grid::-webkit-scrollbar {
  display: none;
}

.image-container {
  flex: 0 0 auto;
  width: 800px;
  height: 1000px;
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: #f5f5f7;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  scroll-snap-align: start;
  animation: imageSlideIn 0.8s ease-out backwards;
}

@keyframes imageSlideIn {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.image-container:nth-child(1) { animation-delay: 0.1s; }
.image-container:nth-child(2) { animation-delay: 0.2s; }
.image-container:nth-child(3) { animation-delay: 0.3s; }
.image-container:nth-child(4) { animation-delay: 0.4s; }
.image-container:nth-child(5) { animation-delay: 0.5s; }

.image-container:hover {
  transform: translateY(-20px) rotate(-2deg) scale(1.02);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  margin: 0 auto;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  filter: brightness(1);
}

.image-container:hover .product-image {
  transform: scale(1.1);
  filter: brightness(1.1);
}

.content-wrapper {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif;
}

.content-wrapper :deep(.content-paragraph) {
  font-size: 21px;
  line-height: 1.5;
  margin-bottom: 1.5em;
  color: #1d1d1f;
  opacity: 0;
  transform: translateY(20px);
  animation: textFadeIn 0.8s ease-out forwards;
  animation-delay: calc(var(--paragraph-index, 0) * 0.2s);
  transition: all 0.3s ease;
  padding-left: 10px;
  border-left: 3px solid transparent;
}

.content-wrapper :deep(.content-paragraph):hover {
  transform: translateX(10px);
  border-left-color: #0071e3;
  color: #000;
}

@keyframes textFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-wrapper :deep(h2) {
  font-size: 48px;
  font-weight: 600;
  margin-bottom: 24px;
  background: linear-gradient(90deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s ease forwards;
}

.content-wrapper :deep(strong) {
  color: #2997ff;
  font-weight: 600;
}

.content-wrapper :deep(ul) {
  list-style: none;
  padding: 0;
  margin: 20px 0;
}

.content-wrapper :deep(li) {
  font-size: 19px;
  line-height: 1.4;
  margin-bottom: 12px;
  padding-left: 24px;
  position: relative;
}

.content-wrapper :deep(li)::before {
  content: "•";
  color: #2997ff;
  position: absolute;
  left: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 视频区域样式优化 */
.video-section {
  margin: 100px 0;  /* 上下增加间距 */
  max-width: none;
  padding: 0;
  position: relative;
  width: 100%;
  height: 93vh;
  background: #f5f5f7;
  overflow: hidden;  /* 防止滚动溢出 */
  opacity: 0;
  animation: fadeInScale 1s ease-out forwards;
  animation-delay: 0.2s;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* 视频容器样式 */
.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  background: #f5f5f7;
  box-shadow: none;
  pointer-events: none;  /* 防止iframe捕获滚动事件 */
  transition: transform 0.3s ease;
}

.video-wrapper:hover {
  transform: scale(1.02);
}

/* iframe样式 */
.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  object-fit: cover;
  pointer-events: auto;  /* 允许视频交互 */
}

/* 移除悬浮效果 */
.video-wrapper:hover {
  transform: none;
  box-shadow: none;
}

.contact-section {
  margin-top: 60px;  /* 增加与上一个区块的间距 */
  text-align: center;
  padding: 60px 20px;
  background: linear-gradient(180deg, #f5f5f7 0%, #fff 100%);
  position: relative;
  overflow: hidden;
  border-radius: 40px;
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
  animation-delay: 0.3s;
  z-index: 2;
}

.contact-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent);
}

.contact-section h2 {
  font-size: 40px;
  margin-bottom: 16px;
  color: #1d1d1f;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.contact-section h2:hover {
  transform: scale(1.05);
  color: #0071e3;
}

.contact-section p {
  font-size: 21px;
  color: #6e6e73;
  margin-bottom: 30px;
  animation: textWave 3s ease-in-out infinite;
  position: relative;
  z-index: 1;
}

@keyframes textWave {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

.contact-btn {
  font-size: 17px;
  padding: 18px 31px;
  border-radius: 980px;
  background: linear-gradient(135deg, #0071e3 0%, #005bb5 100%);
  color: #fff;
  border: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0,113,227,0.3);
  transform-origin: center;
  position: relative;
  overflow: hidden;
  z-index: 10;
  cursor: pointer;
}

.contact-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.contact-btn:hover::before {
  width: 300px;
  height: 300px;
}

.contact-btn:hover {
  transform: translateY(-4px) scale(1.08);
  background: linear-gradient(135deg, #005bb5 0%, #004a99 100%);
  box-shadow: 0 8px 24px rgba(0,113,227,0.4);
}

.contact-btn:active {
  transform: translateY(-2px) scale(1.02);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .hero-section {
    height: 90vh;  /* 稍微减小高度 */
    margin-bottom: 60px;  /* 减小底部间距 */
    padding: 0 20px;  /* 添加水平内边距 */
  }

  .hero-title {
    font-size: 32px;  /* 减小标题字体 */
    padding: 0;
    margin-bottom: 30px;
  }

  /* 价格容器移动端样式 */
  .price-container {
    margin: 16px 0 24px;
  }

  .price-tag {
    font-size: 36px;
  }

  .currency {
    font-size: 24px;
  }

  .price-details {
    flex-direction: row;  /* 保持水平排列 */
    flex-wrap: wrap;  /* 允许换行 */
    justify-content: center;
    gap: 12px;
    font-size: 14px;
  }

  .original-price {
    font-size: 14px;
  }

  .discount-tag {
    font-size: 12px;
    padding: 3px 8px;
  }

  /* 按钮移动端样式 */
  .cta-buttons {
    flex-direction: column;  /* 垂直排列 */
    gap: 12px;
    width: 100%;  /* 占满宽度 */
    max-width: 280px;  /* 限制最大宽度 */
  }

  .cart-btn {
    width: 100%;  /* 按钮占满容器宽度 */
    font-size: 16px;
    padding: 12px 24px;
  }

  .cart-btn i {
    font-size: 18px;
  }

  /* 背景图片移动端适配 */
  .hero-section::after {
    background-size: cover;  /* 改为 cover 以确保填满 */
    background-position: center;
    opacity: 0.4;  /* 稍微降低透明度 */
  }

  .image-grid {
    gap: 20px;
    padding: 0 20px;
  }

  .image-container {
    width: 300px;
    height: 450px;
  }

  .product-image {
    width: 100%;
    height: 100%;
  }
}

/* 小屏幕适配 */
@media (max-width: 375px) {
  .hero-title {
    font-size: 28px;
  }

  .price-tag {
    font-size: 32px;
  }

  .currency {
    font-size: 22px;
  }
}

/* 暗色模式适配 */
@media (prefers-color-scheme: dark) {
  .slide-content {
    background: rgba(0, 0, 0, 0.8);
  }
}

/* 产品详情板块样式 */
.product-details {
  margin: 60px 0;  /* 减小上下边距 */
  background: linear-gradient(180deg, #fff 0%, #f5f5f7 100%);
  position: relative;
  overflow: hidden;
}

/* 内容容器样式 */
.content-container {
  max-width: 100%;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 40px;
  padding: 80px;
  box-shadow: 0 30px 60px rgba(0,0,0,0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-container:hover {
  transform: translateY(-5px);
  box-shadow: 0 40px 80px rgba(0,0,0,0.15);
  border-color: rgba(0, 0, 0, 0.12);
}

/* 内容样式 */
.content-wrapper :deep(.content-paragraph) {
  font-size: 21px;
  line-height: 1.6;
  margin-bottom: 2em;
  color: #1d1d1f;
  letter-spacing: 0.012em;
  font-weight: 400;
}

.content-wrapper :deep(h2) {
  font-size: 56px;
  font-weight: 600;
  margin-bottom: 40px;
  color: #1d1d1f;
  letter-spacing: -0.003em;
  line-height: 1.1;
}

.content-wrapper :deep(strong) {
  color: #2997ff;
  font-weight: 600;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .product-details {
    margin: 40px 0;
    padding: 60px 15px;
  }

  .content-container {
    padding: 40px 24px;
    border-radius: 30px;
  }

  .content-wrapper :deep(.content-paragraph) {
    font-size: 17px;
    line-height: 1.5;
    margin-bottom: 1.5em;
  }

  .content-wrapper :deep(h2) {
    font-size: 40px;
    margin-bottom: 30px;
  }
}

/* 添加渐入动画 */
.content-container {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInContent 1s ease-out forwards;
  animation-delay: 0.2s;
}

@keyframes fadeInContent {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 添加动画效果 */
.hero-section:hover::after {
  opacity: 0.5;  /* 保持固定透明度 */
  transform: none;  /* 移除缩放效果 */
}

/* 添加渐入动画 */
.product-showcase.appear {
  animation: fadeIn 1s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 价格容器样式 */
.price-container {
  position: relative;
  z-index: 2;
  margin: 20px 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  opacity: 0;  /* 初始透明 */
  animation: priceSlideIn 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: 0.3s;  /* 在标题之后显示 */
}

@keyframes priceSlideIn {
  0% {
    opacity: 0;
    transform: translateX(-50px) rotate(-5deg);
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotate(0);
  }
}

/* 价格标签样式 */
.price-tag {
  display: flex;
  align-items: baseline;
  font-size: 48px;
  font-weight: 600;
  color: #1d1d1f;
  text-shadow: none;
  transition: all 0.3s ease;
}

.price-tag:hover {
  transform: scale(1.1);
  color: #0071e3;
}

.currency {
  font-size: 32px;
  margin-right: 4px;
  font-weight: 500;
  animation: numberPop 0.5s ease-out 0.5s backwards;
}

.amount {
  letter-spacing: -0.02em;
  display: inline-block;
  animation: numberPop 0.5s ease-out 0.6s backwards;
}

@keyframes numberPop {
  0% {
    transform: scale(0.5) rotate(-10deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(5deg);
  }
  100% {
    transform: scale(1) rotate(0);
    opacity: 1;
  }
}

/* 价格详情样式 */
.price-details {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 17px;
  color: #6e6e73;
}

.original-price {
  text-decoration: line-through;
  opacity: 0.8;
  color: #1d1d1f;
}

.discount-tag {
  background: #2997ff;
  color: #fff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(41, 151, 255, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(41, 151, 255, 0);
  }
}

.discount-tag:hover {
  transform: scale(1.15) rotate(-5deg);
  background: #0071e3;
  animation: none;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .price-container {
    margin: 16px 0 32px;
  }

  .price-tag {
    font-size: 36px;
  }

  .currency {
    font-size: 24px;
  }

  .price-details {
    font-size: 15px;
    flex-direction: column;
    gap: 8px;
  }

  .discount-tag {
    font-size: 13px;
    padding: 3px 8px;
  }
}

/* 添加滚动触发动画类 */
.scroll-trigger {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.scroll-trigger.visible {
  opacity: 1;
  transform: translateY(0);
}

/* 预览对话框样式 */
:deep(.preview-dialog) {
  background: rgba(0, 0, 0, 0.9);
  border: none;
  border-radius: 0;
  margin: 0 !important;
  padding: 0;
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100% !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  transform: none !important;
}

:deep(.preview-dialog .el-dialog__header),
:deep(.preview-dialog .el-dialog__body) {
  padding: 0;
  margin: 0;
}

.preview-image {
  width: 100%;
  height: 100vh;
  object-fit: contain;
  cursor: zoom-out;
  display: block;
  margin: 0 auto;
  position: relative;
  z-index: 2000;
}

/* 添加关闭按钮样式 */
.preview-close {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2001;
}

.preview-close:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.1);
}

.preview-controls {
  position: fixed;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 0 20px;
  pointer-events: none;
  z-index: 2001;
}

.preview-nav-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  width: 50px;
  height: 50px;
  pointer-events: auto;
  transition: all 0.3s ease;
  z-index: 2001;
  cursor: pointer;
  position: absolute;
}

.preview-nav-btn:first-child {
  left: 20px;
}

.preview-nav-btn:last-child {
  right: 20px;
}

@media (max-width: 768px) {
  .preview-controls {
    padding: 0 10px;
  }

  .preview-nav-btn {
    width: 40px;
    height: 40px;
  }

  .preview-nav-btn:first-child {
    left: 10px;
  }

  .preview-nav-btn:last-child {
    right: 10px;
  }

  .preview-close {
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
  }
}

.gallery-controls {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

.gallery-nav-btn {
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.2);
  color: #1d1d1f;
  width: 50px;
  height: 50px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.gallery-nav-btn:hover {
  background: rgba(0, 0, 0, 0.15);
  transform: scale(1.2) rotate(5deg);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.gallery-nav-btn:active {
  transform: scale(0.95);
}

.gallery-nav-btn i {
  font-size: 20px;
  transition: transform 0.3s ease;
}

.gallery-nav-btn:hover i {
  animation: arrowBounce 0.6s ease-in-out infinite;
}

@keyframes arrowBounce {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(5px); }
}

.gallery-nav-btn:first-child:hover i {
  animation: arrowBounceLeft 0.6s ease-in-out infinite;
}

@keyframes arrowBounceLeft {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(-5px); }
}

@media (max-width: 768px) {
  .gallery-controls {
    margin-top: 20px;
    gap: 15px;
  }

  .gallery-nav-btn {
    width: 40px;
    height: 40px;
  }

  .gallery-nav-btn i {
    font-size: 16px;
  }
}

.product-nav {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 20px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.8);
  padding: 10px 20px;
  border-radius: 30px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.nav-btn {
  background: rgba(0, 0, 0, 0.05) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  color: #1d1d1f !important;
  width: 40px;
  height: 40px;
  transition: all 0.3s ease;
}

.nav-btn:hover {
  background: rgba(0, 0, 0, 0.1) !important;
  transform: scale(1.1);
}

.product-counter {
  color: #1d1d1f;
  font-size: 16px;
  min-width: 60px;
  text-align: center;
}

@media (max-width: 768px) {
  .product-nav {
    bottom: 20px;
    padding: 8px 16px;
  }

  .nav-btn {
    width: 36px;
    height: 36px;
  }

  .product-counter {
    font-size: 14px;
    min-width: 50px;
  }
}

.product-section {
  margin-bottom: 100px;
  opacity: 0;
  transform: translateY(50px) scale(0.95);
  animation: sectionFadeIn 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  position: relative;
  z-index: 2;
}

@keyframes sectionFadeIn {
  0% {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.product-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 80px 0;
  padding: 0 20px;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent);
}

.divider-text {
  color: #6e6e73;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 2px;
  padding: 0 20px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 调整各个部分的间距 */
.hero-section {
  margin-bottom: 60px;
}

.video-section {
  margin-bottom: 60px;
}

.product-showcase {
  margin-bottom: 60px;
}

.product-details {
  margin-bottom: 60px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .product-section {
    margin-bottom: 60px;
  }

  .product-divider {
    margin: 40px 0;
  }

  .divider-text {
    font-size: 14px;
    padding: 0 15px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #6e6e73;
  position: relative;
}

.loading-spinner {
  position: relative;
  width: 100px;
  height: 100px;
  margin-bottom: 30px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #0071e3;
  border-radius: 50%;
  animation: spin 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00d4ff;
  animation-duration: 2s;
  animation-direction: reverse;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #667eea;
  animation-duration: 2.5s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 15px;
  animation: loadingFade 2s ease-in-out infinite;
}

@keyframes loadingFade {
  0%, 100% {
    opacity: 1;
    transform: translateY(0);
  }
  50% {
    opacity: 0.5;
    transform: translateY(-5px);
  }
}

.loading-dots {
  display: flex;
  gap: 8px;
}

.loading-dots span {
  width: 12px;
  height: 12px;
  background: #0071e3;
  border-radius: 50%;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots span:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotBounce {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.3);
    opacity: 1;
  }
}
</style>
